import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useHome } from "../translations/home";

const COL = {
  bubbleBg: "#F4F8FE",
  bubbleTxt: "#0F172A",
  bubbleBrd: "rgba(255,255,255,.25)",
  wallFill: "rgba(140,180,255,.08)",
  wallEdge: "rgba(150,190,255,.45)",
  wallHatch: "rgba(150,190,255,.14)",
  partGray: "rgba(148,163,184,.85)",
  partBlue: "#5B9BFF",
  outTxt: "#FFFFFF",
  outLvl: "#34D399",
  shield: "#5B9BFF",
  label: "#7C93B8",
  ripple: "rgba(91,155,255,.6)",
};

/** Le mur : la bulle reste lisible tout le trajet, se dissout au contact, l'observation sort en texte net. */
export const WallParticles = () => {
  const { language } = useLanguage();
  const v = useHome(language).wall.viz;
  const vRef = useRef(v);
  vRef.current = v;

  const canvasRef = useRef(null);
  const queueRef = useRef([]);
  const [reduced, setReduced] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (reduced) return;
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const DPR = Math.min(2, window.devicePixelRatio || 1);
    let W = 0;
    let H = 400;

    const resize = () => {
      W = cv.parentElement.clientWidth;
      H = W < 640 ? 330 : 400;
      cv.width = W * DPR;
      cv.height = H * DPR;
      cv.style.height = `${H}px`;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let idx = 0;
    let phase = "slide";
    let t0 = performance.now();
    let parts = [];
    let outs = [];
    let ripples = [];
    let placedOut = false;
    let cur = vRef.current.msgs[0];
    let raf;

    const wallX = () => Math.round(W * (W < 640 ? 0.56 : 0.5)) - 13;
    const WALLW = 26;
    const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const fontS = () => (W < 640 ? 13 : 15);

    const rr = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    };

    let bubbleFont = 15;

    /** Réduit puis tronque le texte pour qu'il tienne à gauche de la paroi. */
    const fitBubble = (text) => {
      const max = wallX() - 14;
      let size = fontS();
      let t = text;
      while (size > 10) {
        ctx.font = `500 ${size}px Inter, system-ui, sans-serif`;
        if (ctx.measureText(t).width + 40 <= max) break;
        size -= 1;
      }
      ctx.font = `500 ${size}px Inter, system-ui, sans-serif`;
      if (ctx.measureText(t).width + 40 > max) {
        while (t.length > 10 && ctx.measureText(`${t}… »`).width + 40 > max) t = t.slice(0, -2);
        t = `${t.replace(/[\s«»]*$/, "")}… »`;
      }
      bubbleFont = size;
      return { text: t, width: ctx.measureText(t).width + 40 };
    };

    const bubble = (x, y, text, alpha, conf) => {
      ctx.globalAlpha = alpha;
      ctx.font = `500 ${bubbleFont}px Inter, system-ui, sans-serif`;
      const w = ctx.measureText(text).width + 40;
      ctx.fillStyle = COL.bubbleBg;
      ctx.strokeStyle = COL.bubbleBrd;
      ctx.lineWidth = 1;
      rr(x, y - 22, w, 44, 14);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = COL.bubbleTxt;
      ctx.textAlign = "left";
      ctx.fillText(text, x + 20, y + 5);
      if (conf) {
        ctx.font = "600 12px Inter, system-ui, sans-serif";
        ctx.fillStyle = COL.shield;
        ctx.fillText(vRef.current.confLabel, x, y - 32);
      }
      ctx.globalAlpha = 1;
      return w;
    };

    const drawWall = (now) => {
      const wx = wallX();
      ctx.fillStyle = COL.wallFill;
      ctx.fillRect(wx, 20, WALLW, H - 60);
      ctx.strokeStyle = COL.wallEdge;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(wx, 20);
      ctx.lineTo(wx, H - 40);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(wx + WALLW, 20);
      ctx.lineTo(wx + WALLW, H - 40);
      ctx.stroke();
      ctx.strokeStyle = COL.wallHatch;
      for (let y = 36; y < H - 56; y += 22) {
        const wob = Math.sin(now / 1500 + y / 38) * 2;
        ctx.beginPath();
        ctx.moveTo(wx + 6 + wob, y);
        ctx.lineTo(wx + WALLW - 6 + wob, y + 9);
        ctx.stroke();
      }
      ripples.forEach((rp) => {
        ctx.globalAlpha = rp.a;
        ctx.strokeStyle = COL.ripple;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(wx + WALLW / 2, rp.y, rp.r, 0, 7);
        ctx.stroke();
        rp.r += 1.5;
        rp.a -= 0.022;
        ctx.globalAlpha = 1;
      });
      ripples = ripples.filter((r) => r.a > 0);
    };

    const burst = (x, y, w) => {
      parts = [];
      for (let i = 0; i < 160; i++) {
        parts.push({
          x: x + Math.random() * w,
          y: y + (Math.random() - 0.5) * 38,
          vx: 0.8 + Math.random() * 2,
          vy: (Math.random() - 0.5) * 1.3,
          r: 1 + Math.random() * 2,
          life: 1,
          cross: i < 96,
        });
      }
      ripples.push({ y, r: 5, a: 0.7 });
    };

    const drawOuts = (now) => {
      const wx = wallX();
      const ox = wx + WALLW + (W < 640 ? 14 : 46);
      outs.forEach((o) => {
        const age = (now - o.born) / 1000;
        const a = Math.min(1, age / 0.5) * (o.fad ? Math.max(0, 1 - (now - o.fs) / 900) : 1);
        ctx.globalAlpha = a;
        ctx.textAlign = "left";
        const avail = W - ox - 6;
        let nameSize = W < 640 ? 13 : 17;
        let name = o.name;
        while (nameSize > 10) {
          ctx.font = `600 ${nameSize}px Inter, system-ui, sans-serif`;
          if (ctx.measureText(name).width <= avail) break;
          nameSize -= 1;
        }
        ctx.font = `600 ${nameSize}px Inter, system-ui, sans-serif`;
        while (name.length > 6 && ctx.measureText(`${name}…`).width > avail) name = name.slice(0, -2);
        if (name !== o.name) name = `${name}…`;
        ctx.fillStyle = COL.outTxt;
        ctx.fillText(name, ox, o.y);
        ctx.font = `500 ${W < 640 ? 12 : 13}px Inter, system-ui, sans-serif`;
        ctx.fillStyle = COL.outLvl;
        ctx.fillText(`● ${o.lvl}`, ox, o.y + 21);
        ctx.globalAlpha = 1;
      });
      outs = outs.filter((o) => !(o.fad && now - o.fs > 900));
    };

    const advance = (now) => {
      idx++;
      const msgs = vRef.current.msgs;
      cur = queueRef.current.length ? queueRef.current.shift() : msgs[idx % msgs.length];
      phase = "slide";
      t0 = now;
      placedOut = false;
      parts = [];
      const row = idx % 3;
      outs.forEach((o) => {
        if (o.row === row && !o.fad) {
          o.fad = true;
          o.fs = now;
        }
      });
      const live = outs.filter((o) => !o.fad);
      if (live.length >= 3) {
        live[0].fad = true;
        live[0].fs = now;
      }
    };

    const frame = (now) => {
      ctx.clearRect(0, 0, W, H);
      drawWall(now);
      drawOuts(now);

      const el = (now - t0) / 1000;
      const fitted = fitBubble(cur.text);
      const bw = fitted.width;
      const shown = fitted.text;
      const y = (W < 640 ? 90 : 105) + (idx % 3) * (W < 640 ? 76 : 82);
      const wx = wallX();

      if (phase === "slide") {
        const p = Math.min(1, el / 1.9);
        const tx = cur.conf ? wx - bw - 84 : wx - bw - 8;
        const x = -bw + (tx + bw) * ease(p);
        bubble(x, y, shown, 1, cur.conf);
        if (p >= 1) {
          phase = cur.conf ? "repel" : "pause";
          t0 = now;
        }
      } else if (phase === "pause") {
        bubble(wx - bw - 8, y, shown, 1, false);
        if (el >= 0.45) {
          burst(wx - bw - 8, y, bw);
          phase = "diss";
          t0 = now;
        }
      } else if (phase === "repel") {
        const p = Math.min(1, el / 1.5);
        const x = wx - bw - 84 - 300 * easeOut(p);
        ctx.globalAlpha = Math.max(0, 1 - p * 1.4);
        ctx.strokeStyle = COL.shield;
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.arc(wx - 38, y, 38, Math.PI * 0.55, Math.PI * 1.45);
        ctx.stroke();
        ctx.font = "600 14px Inter, system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillStyle = COL.shield;
        ctx.fillText(vRef.current.neverRead, wx - 38, y - 52);
        ctx.globalAlpha = 1;
        bubble(x, y, shown, 1, true);
        if (p >= 1) advance(now);
      } else if (phase === "diss") {
        const targetX = Math.min(wx + WALLW + 120, W - 40);
        const targetY = y + 4;
        parts.forEach((pt) => {
          if (pt.cross) {
            if (pt.x < wx + WALLW + 4) {
              pt.x += pt.vx * 1.7;
              pt.y += pt.vy;
            } else {
              pt.x += (targetX + Math.random() * 140 - pt.x) * 0.07;
              pt.y += (targetY - pt.y) * 0.07;
              if (el > 1.6) pt.life -= 0.05;
            }
          } else {
            pt.x += pt.vx * 0.55;
            pt.y += pt.vy;
            if (pt.x > wx - 2) {
              pt.x = wx - 2;
              pt.life -= 0.04;
            } else pt.life -= 0.007;
          }
          if (pt.life > 0) {
            ctx.globalAlpha = pt.life;
            ctx.fillStyle = pt.cross ? COL.partBlue : COL.partGray;
            ctx.beginPath();
            ctx.arc(pt.x, pt.y, pt.r, 0, 7);
            ctx.fill();
            ctx.globalAlpha = 1;
          }
        });
        if (el > 1.5 && !placedOut && cur.out) {
          placedOut = true;
          outs.push({ name: cur.out, lvl: cur.lvl, y: targetY, row: idx % 3, born: now, fad: false });
        }
        if (el > 3.1) advance(now);
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [reduced, language]);

  const send = (e) => {
    e.preventDefault();
    const value = input.trim();
    if (!value) {
      setError(true);
      return;
    }
    setError(false);
    queueRef.current.push({ text: `« ${value} »`, out: v.userObs, lvl: v.userLevel, conf: false });
    setResult({ obs: v.userObs, level: v.userLevel });
    setInput("");
  };

  return (
    <div className="bg-[#0B1D3A] rounded-2xl overflow-hidden" data-testid="wall-animation">
      <div className="px-5 py-7 sm:px-9 sm:py-8">
        <div className="flex items-baseline justify-between mb-1">
          <span className="mono-cayus text-[#7C93B8]">{v.in}</span>
          <span className="mono-cayus text-[#9EC2FF]">{v.wall}</span>
          <span className="mono-cayus text-[#7C93B8]">{v.out}</span>
        </div>

        {reduced ? (
          <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center py-8" data-testid="wall-static">
            <div className="flex flex-col gap-2">
              {v.msgs.map((m, i) => (
                <span
                  key={i}
                  className="text-xs sm:text-sm text-[#0F172A] bg-[#F4F8FE] rounded-lg px-3 py-2 truncate"
                  data-testid={`wall-in-${i}`}
                >
                  {m.text}
                </span>
              ))}
            </div>
            <div className="w-[26px] h-32 rounded bg-[rgba(140,180,255,.12)] border-x border-[rgba(150,190,255,.45)]" />
            <div className="flex flex-col gap-2">
              {v.msgs.map((m, i) => (
                <span key={i} className="text-xs sm:text-sm font-semibold text-white" data-testid={`wall-out-${i}`}>
                  {m.out ? (
                    <>
                      {m.out} · <span className="text-[#34D399]">{m.lvl}</span>
                    </>
                  ) : (
                    <span className="text-[#5B9BFF]">{v.neverRead}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="relative">
            <canvas ref={canvasRef} className="w-full block" data-testid="wall-canvas" aria-label={v.label} />
          </div>
        )}

        <form onSubmit={send} data-testid="wall-try-form">
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <input
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setError(false);
              }}
              maxLength={48}
              placeholder={result ? v.placeholderAfter : v.demoLabel}
              className="w-full sm:flex-1 h-12 shrink-0 rounded-[10px] border border-[#2A4368] bg-[#0E2240] px-4 text-[15px] text-[#EAF1FB] placeholder:text-[#7E96BC] focus:outline-none focus:border-[#4C82E0]"
              data-testid="wall-input"
            />
            <button
              type="submit"
              className="btn-cayus h-12 rounded-[10px] px-6 bg-[#2563EB] text-white text-[15px] font-semibold hover:bg-[#1D4ED8]"
              data-testid="wall-send-button"
            >
              {v.button}
            </button>
          </div>

          {error && (
            <p className="text-[13px] text-[#F87171] mt-2" data-testid="wall-error">
              {v.err}
            </p>
          )}
          {result && (
            <p className="text-sm font-semibold text-white mt-3" data-testid="wall-try-result">
              {result.obs} · <span className="text-[#34D399]">{result.level}</span>
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
            <span className="text-[12.5px] text-[#7C93B8]" data-testid="wall-privacy-note">
              {v.privacy}
            </span>
            <span className="mono-cayus text-[#FBBF24]" data-testid="wall-kept-counter">
              {v.counter}
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WallParticles;
